/**
 * utils/invoiceNumbers.js
 * Pure invoice-number minting (Phase T2a, PRD §4). Pusat and cabang draw from entirely
 * separate formats and counters — there is no longer a single shared `mintInvoiceNumber`.
 *
 * Numbers are immutable once minted: editing a transaction never re-mints one — with exactly
 * ONE scoped exception (App.js's `editTransaction`, the salesperson-correction re-mint
 * feature): `mintCabangNumber` IS now also called from there, specifically when an edit
 * reassigns a CABANG-anchored transaction's salesperson to someone from a genuinely different
 * branch. This module's own functions are unchanged by that exception — they stay pure,
 * stateless "read current count, increment, return" operations with no per-transaction memory,
 * so calling `mintCabangNumber` a second (or third) time for the same underlying transaction
 * across separate edits is safe by construction, not something this module needs to guard
 * against itself. Counters never decrement, so a deleted transaction — or an old, now-orphaned
 * number abandoned by a re-mint — both leave a legitimate gap in the sequence, never a bug.
 */

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

/** 1..12 -> "I".."XII". */
export function romanMonth(n) {
  return ROMAN[n - 1];
}

/** "YYYY-MM-DD" -> "YYYY-MM" */
const monthKeyOf = (dateStr) => dateStr.slice(0, 7);

/**
 * Mint the next PUSAT invoice number for a transaction dated `dateStr`.
 * Format: NNNN/IJM/MM/YYYY — serial zero-padded to 4, "IJM" hardcoded, month/year of the
 * TRANSACTION date. ONE sequence per YYYY-MM in `data.txnCounters.pusat`, shared by every
 * pusat-anchored Penjualan/Transfer regardless of salesperson.
 *
 * Pure — returns the new FULL txnCounters object (both sub-maps), never mutates.
 * @returns {{ invoiceNumber: string, updatedCounters: object }}
 */
export function mintPusatNumber(data, dateStr) {
  const counters = (data.txnCounters && data.txnCounters.pusat) || {};
  const key = monthKeyOf(dateStr);
  const serialNum = (counters[key] || 0) + 1;
  const pusat = { ...counters, [key]: serialNum };

  const [year, month] = dateStr.split("-");
  const serial = String(serialNum).padStart(4, "0");
  const invoiceNumber = `${serial}/IJM/${month}/${year}`;

  return {
    invoiceNumber,
    updatedCounters: {
      ...data.txnCounters,
      pusat,
      cabangBySales: (data.txnCounters && data.txnCounters.cabangBySales) || {},
    },
  };
}

/**
 * Mint the next CABANG invoice number for a transaction dated `dateStr`, salesperson code
 * `salespersonCode`. Format: "CODE NNNN/RomanMonth/YY" — e.g. "DC 0007/VI/26". Serial resets
 * MONTHLY per salesperson code, in `data.txnCounters.cabangBySales[CODE][YYYY-MM]` — mirrors
 * `mintPusatNumber`'s own monthly-reset pattern exactly, one level deeper (nested by
 * salesperson code, then by month). Month is a Roman numeral (I..XII); year is two digits.
 *
 * Pure — returns the new FULL txnCounters object (both sub-maps), never mutates.
 * @returns {{ invoiceNumber: string, updatedCounters: object }}
 */
export function mintCabangNumber(data, salespersonCode, dateStr) {
  const counters = (data.txnCounters && data.txnCounters.cabangBySales) || {};
  const key = monthKeyOf(dateStr);
  const monthCounters = counters[salespersonCode] || {};
  const serialNum = (monthCounters[key] || 0) + 1;
  const cabangBySales = { ...counters, [salespersonCode]: { ...monthCounters, [key]: serialNum } };

  const [yearFull, monthStr] = dateStr.split("-");
  const serial = String(serialNum).padStart(4, "0");
  const invoiceNumber = `${salespersonCode} ${serial}/${romanMonth(Number(monthStr))}/${yearFull.slice(2)}`;

  return {
    invoiceNumber,
    updatedCounters: {
      ...data.txnCounters,
      pusat: (data.txnCounters && data.txnCounters.pusat) || {},
      cabangBySales,
    },
  };
}
